/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState, useMemo, useCallback, Fragment, useRef} from "react"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {Icon} from "components/Icon"
import {attendanceService} from "services"
import {getFullName, handleError, roundTwoDigits, getFieldLabel} from "helpers"
import {formatDailyAttendanceHours} from "helpers/attendance"
import styles from "../../common.module.css"
import rollCallStyles from "./RollCall.module.css"
import {cloneDeep, get, isEmpty, uniqBy} from "lodash"
import {useCurrentProfilePermissions, useModel, useMutation, useVisible} from "hooks"
import {AttendanceEvent, AttendanceEventStatus, AttendanceEventType} from "types/attendance"
import {DailyAttendanceTimeScale} from "types/settings/general-setting"
import moment from "moment"
import {useDebounce} from "react-use"
import {useFilterStorage} from "hooks/useFilterStorage"
import {FilterKey} from "types/filter"
import {exportCsv, exportExcel} from "helpers/export-table"
import {ExportFileType} from "components/ui"
import AddManualDailyAttendance from "../AddManualDailyAttendance/AddManualDailyAttendance"
import {useQuery} from "@tanstack/react-query"
import {
    DailyAttendanceFilter,
    DailyAttendanceFilterCheckin,
    DailyAttendanceFilterCheckout
} from "../DailyAttendanceFilter/DailyAttendanceFilter"
import {Checkbox, Spin} from "antd"
import {YesNo} from "types/common"
import {BatchDailyAttendanceUpdates} from "../BatchDailyAttendanceUpdates"
import {BatchDailyAttendanceCheckin} from "../BatchDailyAttendanceCheckin"
import {Permissions} from "@edular/permissions"
import {Settings} from "types/settings"

export enum FilterEventType {
    Active = "active",
    InSchool = "inSchool",
    Break = "break",
    Checkout = "checkOut",
    Missed = "missed"
}

const initialDefaultFilter = {
    programVersions: [],
    filterEventType: undefined,
    campuses: [],
    instructors: [],
    startingTerms: [],
    currentTerms: [],
    studentStatuses: [],
    programs: [],
    groups: [],
    phases: [],
    shifts: [],
    filterCheckin: DailyAttendanceFilterCheckin.None,
    filterCheckout: DailyAttendanceFilterCheckout.None,
    isSpecialEventEnabled: null
}

function RollCallTable(props) {
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        getCurrentData,
        allFields,
        dispatch,
        defaultStudentStatuses = [],
        filter,
        isLoadedTableFuncs,
        date,
        setDate,
        fameSettings,
        orderField
    } = props

    const model = useModel()
    const userCampuses = model.getCurrentUserProfile()?.campuses ?? []

    const defaultFilter = useMemo(() => {
        return {...initialDefaultFilter, studentStatuses: defaultStudentStatuses, campuses: userCampuses}
    }, [defaultStudentStatuses, userCampuses])

    const getFilterMemoryKey = () => {
        return FilterKey.ListRollCall
    }

    const filterKey = getFilterMemoryKey()

    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        filterKey,
        defaultFilter
    )
    const popupAddManualDailyAttendance = useVisible()
    const popupBatchDailyAttendanceUpdates = useVisible()
    const popupBatchDailyAttendanceCheckin = useVisible()
    const [exporting, setExporting] = useState(false)
    const [batchFilterParams, setBatchFilterParams] = useState<Record<string, any>>({})

    const {studentProfileId, statsInfo} = props
    const {eventType: filterEventType, majorVersionIds, phaseIds, shiftIds, campusIds, programIds} = filter
    const [searchDebounce, setSearchDebounce] = useState(searchValue)
    const currentProfilePermissions = useCurrentProfilePermissions()

    const canAddAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const listRollCallQuery = useQuery(
        [
            "list-roll-call",
            page,
            pageSize,
            filter,
            searchDebounce,
            statsInfo,
            date,
            canAddAttendance,
            orderField?.field,
            orderField?.order
        ],
        async () => {
            const params = getFilterParams()
            const {data, total} = await attendanceService.getListRollCall({
                range: {
                    page,
                    pageSize
                },
                sort: orderField?.field ? {orderBy: orderField.field, orderDir: orderField.order} : undefined,
                ...params
            })

            const students = (data || []).map((item) => {
                return normalizeItem(item)
            })
            return {
                data: students,
                total
            }
        },
        {
            enabled: isLoadedTableFuncs,
            onSuccess: (data) => {
                props.dispatch({data: data.data, total: data.total})
            },
            onError: (err) => {
                handleError(err)
            },
            initialData: {
                data: [],
                total: 0
            }
        }
    )

    const creatingProfileIdRef = useRef<number | null>(null)

    const createDailyAttendanceQuery = useMutation(
        async (payload: {
            eventType: AttendanceEventType
            majorVersionId: number
            profileId: number
            campusId: number
            timezone: string
        }) => {
            const {eventType, majorVersionId, profileId, campusId, timezone} = payload
            const filterDate = moment(date)
            const createdAt = moment()
            createdAt.set({
                date: filterDate.date(),
                month: filterDate.month(),
                year: filterDate.year()
            })

            const params = {
                majorVersionId,
                eventType: eventType,
                profileId,
                campusId,
                message: "Manual create daily attendance",
                createdAt: moment(createdAt).tz(timezone, true).toISOString()
            }
            const response = await attendanceService.createDailyAttendance(params)
            return {response, profileId, eventType}
        },
        {
            onSuccess({response, profileId, eventType}) {
                const createdAttendance = response?.data ?? response
                const attendanceId = createdAttendance?.attendanceId
                if (!createdAttendance || !attendanceId) {
                    listRollCallQuery.refetch()
                    return
                }

                const data = getCurrentData()
                const newData = data.map((row) => {
                    if (row.id !== profileId && row.profileId !== profileId) {
                        return row
                    }
                    return normalizeItem(mergeCreatedAttendanceIntoRow(row, createdAttendance, eventType))
                })
                props.dispatch({data: newData})
            },
            onError: (err, variables) => {
                handleError(err)
                const profileId = variables?.profileId
                if (!profileId) {
                    return
                }
                const data = getCurrentData()
                const newData = data.map((row) => {
                    if (row.id !== profileId && row.profileId !== profileId) {
                        return row
                    }
                    return normalizeItem(row)
                })
                props.dispatch({data: newData})
            },
            onSettled: () => {
                creatingProfileIdRef.current = null
            }
        }
    )

    const mergeCreatedAttendanceIntoRow = (
        row: any,
        createdAttendance: any,
        fallbackEventType: AttendanceEventType
    ) => {
        const eventType = (createdAttendance.eventType ?? fallbackEventType) as AttendanceEventType
        const attendanceId = createdAttendance.attendanceId
        if (!attendanceId) {
            return row
        }

        const newEvent: AttendanceEvent = {
            id: createdAttendance.id,
            eventType,
            createdAt: createdAttendance.createdAt,
            attendanceId,
            location: createdAttendance.location ?? "",
            status: createdAttendance.status ?? AttendanceEventStatus.Success,
            message: createdAttendance.message,
            photoUrl: createdAttendance.photoUrl
        }

        const attendances = cloneDeep(row.attendances ?? [])
        const groupIndex = attendances.findIndex((item) => item.attendanceId === attendanceId)
        if (groupIndex === -1) {
            attendances.push({
                attendanceId,
                attendedHours: createdAttendance.attendedHours ?? 0,
                events: [newEvent]
            })
        } else {
            const group = attendances[groupIndex]
            const eventExists = (group.events ?? []).some((event) => event.id && event.id === newEvent.id)
            if (!eventExists) {
                group.events = [...(group.events ?? []), newEvent]
            }
            if (typeof createdAttendance.attendedHours === "number") {
                group.attendedHours = createdAttendance.attendedHours
            }
        }

        return {
            ...row,
            attendances,
            latestEventType: eventType,
            attendedHours:
                typeof createdAttendance.attendedHours === "number"
                    ? createdAttendance.attendedHours
                    : row.attendedHours,
            checkinCreatedAt:
                eventType === AttendanceEventType.Checkin
                    ? row.checkinCreatedAt ?? createdAttendance.createdAt
                    : row.checkinCreatedAt,
            checkoutCreatedAt:
                eventType === AttendanceEventType.Checkout ? createdAttendance.createdAt : row.checkoutCreatedAt
        }
    }

    useEffect(() => {
        props.dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: listRollCallQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    const getFilterParams = () => {
        const currentMajorVersionIds = statsInfo.map((info) => info.id)
        const currentPhaseIds = statsInfo.map((info) => info.phaseId).filter(Boolean)
        const currentShiftIds = statsInfo.map((info) => info.shiftId).filter(Boolean)
        const currentProgramIds = statsInfo.map((info) => info.programId).filter(Boolean)
        const currentCampusIds = statsInfo.map((info) => info.campusId).filter(Boolean)
        const _majorVersionIds =
            majorVersionIds?.filter((majorVersionId) => currentMajorVersionIds.includes(majorVersionId)) ?? []
        const _phaseIds = phaseIds?.filter((phaseId) => currentPhaseIds.includes(phaseId)) ?? []
        const _shiftIds = shiftIds?.filter((shiftId) => currentShiftIds.includes(shiftId)) ?? []
        const _campusIds = campusIds?.filter((campusId) => currentCampusIds.includes(campusId)) ?? []
        const _programIds = programIds?.filter((programId) => currentProgramIds.includes(programId)) ?? []
        const params: any = {
            campusIds: !isEmpty(currentFilter?.campuses)
                ? currentFilter.campuses.map((campus) => campus.id).concat(_campusIds)
                : campusIds,
            instructorProfileIds: !isEmpty(currentFilter?.instructors)
                ? currentFilter.instructors.map((instructor) => instructor.profileId)
                : undefined,
            majorVersionIds: !isEmpty(currentFilter?.programVersions)
                ? currentFilter.programVersions.map((pv) => pv.id).concat(_majorVersionIds)
                : majorVersionIds,
            programIds: !isEmpty(currentFilter?.programs)
                ? currentFilter.programs.map((p) => p.id).concat(_programIds)
                : programIds,
            eventTypes: undefined,
            phaseIds: !isEmpty(currentFilter?.phases)
                ? currentFilter.phases.map((phase) => phase.phaseId).concat(_phaseIds)
                : _phaseIds,
            shiftIds: !isEmpty(currentFilter?.shifts)
                ? currentFilter.shifts.map((shift) => shift.shiftId).concat(_shiftIds)
                : _shiftIds,
            startingTermIds: !isEmpty(currentFilter?.startingTerms)
                ? currentFilter.startingTerms.map((term) => term.id)
                : undefined,
            currentTermIds: !isEmpty(currentFilter?.currentTerms)
                ? currentFilter.currentTerms.map((term) => term.id)
                : undefined,
            statusIds: !isEmpty(currentFilter?.studentStatuses)
                ? currentFilter.studentStatuses.map((status) => status.statusId)
                : undefined,
            groupIds: !isEmpty(currentFilter?.groups) ? currentFilter.groups.map((group) => group.id) : undefined,
            hasCheckin: currentFilter?.filterCheckin === DailyAttendanceFilterCheckin.HasCheckin,
            hasCheckout: currentFilter?.filterCheckout === DailyAttendanceFilterCheckout.HasCheckout,
            isMissingCheckout: currentFilter?.filterCheckout === DailyAttendanceFilterCheckout.MissingCheckout,
            isMissingCheckin: currentFilter?.filterCheckin === DailyAttendanceFilterCheckin.MissingCheckin,
            isOverlappingTime: !!currentFilter?.overlappingTime,
            isLateCheckin: currentFilter?.filterCheckin === DailyAttendanceFilterCheckin.LateCheckin,
            isSpecialEventEnabled:
                typeof currentFilter?.isSpecialEventEnabled === "boolean"
                    ? currentFilter.isSpecialEventEnabled
                    : undefined
        }

        if (date) {
            const filterDate = moment(date)
            params.date = filterDate.format("YYYY-MM-DD")
        } else {
            params.date = moment().format("YYYY-MM-DD")
        }

        if (searchDebounce?.length) {
            params.search = searchDebounce
        }

        if (
            (_majorVersionIds?.length ||
                _phaseIds?.length ||
                _shiftIds?.length ||
                _programIds?.length ||
                _campusIds?.length) &&
            filterEventType
        ) {
            if (filterEventType === FilterEventType.Active) {
                params.eventTypes = []
            } else if (filterEventType === FilterEventType.Break) {
                params.eventTypes = [AttendanceEventType.BreakStart]
            } else if (filterEventType === FilterEventType.Checkout) {
                params.eventTypes = [AttendanceEventType.Checkout]
            } else if (filterEventType === FilterEventType.InSchool) {
                params.eventTypes = [AttendanceEventType.Checkin, AttendanceEventType.BreakEnd]
            } else if (filterEventType === FilterEventType.Missed) {
                params.isMissed = true
            }
        }

        const profileId = model.profileId

        if (!model.isStaffOrAdmin() && profileId) {
            params.studentProfileIds = [profileId]
        } else if (studentProfileId) {
            params.studentProfileIds = [parseInt(studentProfileId)]
        }
        return params
    }

    const renderIconAttendance = (eventType: string) => {
        if (eventType === "check-out") {
            return <Icon icon="CHECKOUT_FILL" color={"#1E90FF"} className={styles.checkOutIcon} />
        }
        if (eventType === "check-in") {
            return <Icon icon="CHECKIN_FILL" color={"#1E90FF"} className={styles.rightIcon} />
        }
        if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        }
        if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        }
        return null
    }

    const onPressInOut = (
        e: React.MouseEvent,
        payload: {
            eventType: AttendanceEventType
            majorVersionId: number
            profileId: number
            campusId: number
            timezone: string
        }
    ) => {
        e.stopPropagation()

        // Ref (not state): checkHtml stores stale onClick closures — state guard would block 2nd click
        if (creatingProfileIdRef.current != null) {
            return
        }

        creatingProfileIdRef.current = payload.profileId
        const data = getCurrentData()
        props.dispatch({
            data: data.map((row) => {
                if (row.id !== payload.profileId && row.profileId !== payload.profileId) {
                    return row
                }
                return {
                    ...row,
                    checkHtml: renderButtonInOut({
                        majorVersionId: payload.majorVersionId,
                        profileId: payload.profileId,
                        campusId: payload.campusId,
                        typeButton: payload.eventType,
                        enabled: false,
                        isLoading: true,
                        timezone: payload.timezone
                    })
                }
            })
        })
        createDailyAttendanceQuery.mutate(payload)
    }

    const renderButtonInOut = (payload: {
        majorVersionId: number
        profileId: number
        campusId: number
        typeButton: AttendanceEventType
        enabled: boolean
        timezone: string
        isLoading?: boolean
    }) => {
        const {majorVersionId, profileId, campusId, typeButton, enabled, timezone, isLoading} = payload
        const canClick = enabled && !isLoading
        return (
            <div
                onClick={(e) =>
                    canClick && onPressInOut(e, {eventType: typeButton, majorVersionId, profileId, campusId, timezone})
                }
                style={{
                    opacity: canClick ? 1 : 0.3,
                    cursor: canClick ? "pointer" : "not-allowed"
                }}
                className={rollCallStyles.buttonOut}>
                {isLoading ? (
                    <div className={rollCallStyles.actionButton} style={{backgroundColor: "#d9d9d9"}}>
                        <Spin size="small" />
                    </div>
                ) : [AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(typeButton) ? (
                    <div className={rollCallStyles.actionButton} style={{backgroundColor: "#18A957"}}>
                        <Icon icon="IN_CLASS" color="white" />
                    </div>
                ) : (
                    <div className={rollCallStyles.actionButton} style={{backgroundColor: "#DF1642"}}>
                        <Icon icon="OUT_CLASS" color="white" />
                    </div>
                )}
            </div>
        )
    }

    const renderAttendanceEventsPanel = useCallback(
        (
            record: {
                attendances?: Array<{attendanceId: number; events: AttendanceEvent[]; attendedHours: number}>
                student?: {timezone?: string}
            },
            expanded: boolean
        ) => {
            const attendances = record.attendances ?? []
            if (!expanded || !attendances.length) {
                return null
            }

            const timezone = record.student?.timezone ?? moment.tz.guess()

            return (
                <div className={rollCallStyles.expandedAttendancePanel}>
                    <div className={rollCallStyles.eventsContainer}>
                        {attendances.map((item, attendanceIndex) => (
                            <Fragment key={item.attendanceId ?? attendanceIndex}>
                                {item.events?.map((event, index) => (
                                    <div
                                        key={event.id ?? `${item.attendanceId}-${index}`}
                                        className={rollCallStyles.eventItem}>
                                        <div className={rollCallStyles.event}>
                                            {event.eventType === AttendanceEventType.Checkin ? (
                                                <Icon
                                                    icon="CHECKIN_FILL"
                                                    className={rollCallStyles.eventIcon}
                                                    color={"#18a957"}
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.Checkout ? (
                                                <Icon
                                                    icon="CHECKOUT_FILL"
                                                    className={rollCallStyles.eventIcon}
                                                    color={"#DF1642"}
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakEnd ? (
                                                <Icon
                                                    icon="ATTENDANCE_IN"
                                                    className={rollCallStyles.eventIcon}
                                                    color={"#18a957"}
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakStart ? (
                                                <Icon
                                                    icon="ATTENDANCE_OUT"
                                                    className={rollCallStyles.eventIcon}
                                                    color={"#DF1642"}
                                                />
                                            ) : null}
                                            <span className={rollCallStyles.eventTime}>
                                                {moment(event.createdAt).tz(timezone).format(model.getUserTimeFormat())}
                                            </span>
                                        </div>
                                        {index !== item.events?.length - 1 ? (
                                            <div className={rollCallStyles.eventSeparator} />
                                        ) : null}
                                    </div>
                                ))}
                                {attendanceIndex !== attendances.length - 1 ? (
                                    <div className={rollCallStyles.eventSeparator} />
                                ) : null}
                            </Fragment>
                        ))}
                    </div>
                </div>
            )
        },
        // model used for time format; HOC table re-renders often — keep deps tight so expand state stays
        []
    )

    const rollCallExpandable = useMemo(
        () => ({
            isExpanded: true,
            rowExpandable: (record) => !!record.attendances?.length,
            expandedRowRender: renderAttendanceEventsPanel
        }),
        [renderAttendanceEventsPanel]
    )

    const normalizeItem = (daily, isExport: boolean = false) => {
        const student = daily?.student ?? {}
        const latestEventType = daily.latestEventType
        const shifts: any[] = uniqBy(student.shifts ?? [], "shiftId")
        const phases: any[] = uniqBy(student.phases ?? [], "phaseId")
        const timezone = student.timezone ?? moment.tz.guess()

        let typeButton: AttendanceEventType = AttendanceEventType.Checkin
        if ([AttendanceEventType.BreakStart, AttendanceEventType.Checkout].includes(latestEventType)) {
            typeButton = AttendanceEventType.Checkin
        } else if ([AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(latestEventType)) {
            typeButton = AttendanceEventType.Checkout
        }

        let result = {
            ...daily,
            phaseNames: phases.map((phase) => phase.name).join(",") ?? "",
            shiftNames: shifts.map((shift) => shift.name).join(",") ?? "",
            id: student.id,
            customUserId: student.customUserId ?? "",
            studentStatusHtml: student.status?.name ?? "",
            statusHtml: isExport ? latestEventType : renderIconAttendance(latestEventType),
            checkHtml: renderButtonInOut({
                majorVersionId: student.currentMajorVersionId?.id,
                profileId: student.id,
                campusId: get(student, ["campuses", 0, "id"]),
                typeButton: typeButton,
                enabled: canAddAttendance && daily.currentDateByStudentTimezone === moment(date).format("YYYY-MM-DD"),
                timezone: student.timezone ?? moment.tz.guess()
            }),
            customProfileId: student?.customProfileId ?? "",
            studentName: getFullName(student),
            firstName: student.firstName ?? "",
            lastName: student.lastName ?? "",
            middleName: student.middleName ?? "",
            userId: student.userId,
            attendedMinutes: roundTwoDigits((daily.attendedHours ?? 0) * 60),
            attended: formatDailyAttendanceHours(
                daily.attendedHours,
                fameSettings?.dailyAttendanceTimeScale ?? DailyAttendanceTimeScale.Minutes
            ),
            checkinHtml: daily.checkinCreatedAt
                ? moment(daily.checkinCreatedAt).tz(timezone).format(model.getUserTimeFormat())
                : "",
            checkoutHtml: daily.checkoutCreatedAt
                ? moment(daily.checkoutCreatedAt).tz(timezone).format(model.getUserTimeFormat())
                : "",
            accommodatedHoursHtml: isExport ? (
                student?.isAccommodatedHoursEnabled ? (
                    YesNo.Yes
                ) : (
                    YesNo.No
                )
            ) : (
                <Checkbox checked={student.isAccommodatedHoursEnabled} disabled />
            ),
            majorVersionHtml: student.currentMajorVersionId?.versionName
                ? `${student.currentMajorVersionId.versionName} (${student.currentMajorVersionId.versionCode})`
                : "",
            majorHtml: student.currentMajor?.name ? `${student.currentMajor.name} (${student.currentMajor.code})` : ""
        }

        return result
    }

    function getColumns() {
        return [
            {
                title: getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
                field: "customProfileId",
                headerStyle: {width: "100px"},
                columnIndex: 1,
                sortable: true,
                orderField: "customProfileId"
            },
            {
                title: "Custom User Id",
                field: "customUserId",
                headerStyle: {width: "100px"},
                columnIndex: 1,
                sortable: true,
                orderField: "customUserId"
            },
            {
                title: "User Id",
                field: "userId",
                headerStyle: {width: "100px"},
                columnIndex: 2,
                sortable: true,
                orderField: "userId"
            },
            {
                title: "First Name",
                field: "firstName",
                headerStyle: {minWidth: "150px"},
                columnIndex: 3,
                sortable: true,
                orderField: "firstName"
            },
            {
                title: "Middle Name",
                field: "middleName",
                headerStyle: {minWidth: "150px"},
                columnIndex: 4
            },
            {
                title: "Last Name",
                field: "lastName",
                headerStyle: {minWidth: "150px"},
                columnIndex: 5,
                sortable: true,
                orderField: "lastName"
            },

            {
                title: "Check",
                field: "checkHtml",
                headerStyle: {width: "80px"},
                columnIndex: 6
            },
            {
                title: "Student Status",
                field: "studentStatusHtml",
                headerStyle: {width: "90px"},
                columnIndex: 7
            },
            {
                title: "Accommodated Hours",
                field: "accommodatedHoursHtml",
                headerStyle: {width: "90px"},
                columnIndex: 8
            },
            {
                title: "Phase",
                field: "phaseNames",
                headerStyle: {width: "120px"},
                columnIndex: 9
            },
            {
                title: "Shift",
                field: "shiftNames",
                headerStyle: {width: "120px"},
                columnIndex: 10
            },
            {
                title: "Major",
                field: "majorHtml",
                headerStyle: {width: "150px"},
                columnIndex: 11
            },
            {
                title: "Major Version",
                field: "majorVersionHtml",
                headerStyle: {width: "150px"},
                columnIndex: 12
            },
            {
                title: "Status",
                field: "statusHtml",
                headerStyle: {width: "80px"},
                columnIndex: 13
            },
            {
                title: "Checkin",
                field: "checkinHtml",
                headerStyle: {width: "90px"},
                columnIndex: 14
            },
            {
                title: "Checkout",
                field: "checkoutHtml",
                headerStyle: {width: "90px"},
                columnIndex: 15
            },
            {
                title: "Attended",
                field: "attended",
                headerStyle: {width: "90px"},
                columnIndex: 16
            }
        ]
    }

    function getFields() {
        return [
            getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
            "Custom User Id",
            "User Id",
            "First Name",
            "Last Name",
            "Student Name",
            "Student Status",
            "Accommodated Hours",
            "Phase",
            "Shift",
            "Major",
            "Major Version",
            "Status",
            "Checkin",
            "Checkout",
            "Attended",
            "Check"
        ]
    }

    const onChangeFilter = (key, value) => {
        const newFilter = {...currentFilter}
        newFilter[key] = value
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        applyFilter()
        listRollCallQuery.refetch()
    }

    const handleExportFile = async (type: ExportFileType) => {
        setExporting(true)
        try {
            const params = getFilterParams()
            const {data} = await attendanceService.getListRollCall({
                range: {
                    page: 1,
                    pageSize: total
                },
                ...params
            })
            const students = (data || []).map((daily) => {
                return normalizeItem(daily, true)
            })
            const exportColumns = cloneDeep([
                {
                    title: "Date",
                    field: "date",
                    headerStyle: {width: "90px"}
                },
                ...columns
            ]).filter((col) => col.title !== "Check")
            const filename = `Roll_Call_${moment().format("MM_DD_YYYY")}`
            const payload = {
                filename,
                columns: exportColumns.map((col) => col.title.toUpperCase()),
                rows: students.map((item) =>
                    exportColumns.map((col) => {
                        if (col.field === "date") {
                            return moment(date).format(model.getUserDateFormat())
                        }
                        return item[col.field]?.toString() ?? ""
                    })
                )
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (err) {
            handleError(err)
        } finally {
            setExporting(false)
        }
    }

    const onUpdateRowData = (updatedData) => {
        const data = getCurrentData()
        const newData = cloneDeep(data).map((item) => {
            if (item.id === updatedData.id) {
                return {
                    ...item,
                    isChecked: updatedData.isChecked
                }
            }
            return item
        })
        dispatch({data: newData})
    }

    return (
        <div style={{paddingBottom: 50}}>
            <DailyAttendanceFilter
                searchValue={searchValue}
                changeSearch={changeSearch}
                onApplyFilter={onApplyFilter}
                clearFilter={clearFilter}
                currentFilter={currentFilter}
                showDailyRoster
                onOpenAddManualDailyAttendancePopup={popupAddManualDailyAttendance.open}
                exporting={exporting}
                handleExportFile={handleExportFile}
                onChangeFilter={onChangeFilter}
                setCurrentFilter={setCurrentFilter}
                date={date}
                onChangeDate={setDate}
                savedFilterOptions={{
                    enabled: true,
                    filterKey: filterKey,
                    setFilter: setCurrentFilter,
                    defaultFilter
                }}
                showBatchUpdates
                onOpenBatchDailyAttendanceUpdatesPopup={(mode = "minutes") => {
                    setBatchFilterParams(getFilterParams())
                    if (mode === "checkin") {
                        popupBatchDailyAttendanceCheckin.open()
                        return
                    }
                    popupBatchDailyAttendanceUpdates.open()
                }}
            />
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={listRollCallQuery.isFetching}
                fields={fields}
                orderField={orderField}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
                expandable={rollCallExpandable}
            />
            <AddManualDailyAttendance
                onRefreshData={listRollCallQuery.refetch}
                onClose={() => {
                    popupAddManualDailyAttendance.close()
                    listRollCallQuery.refetch()
                }}
                isShow={popupAddManualDailyAttendance.isVisible}
            />
            <BatchDailyAttendanceUpdates
                isShow={popupBatchDailyAttendanceUpdates.isVisible}
                onClose={popupBatchDailyAttendanceUpdates.close}
                filterParams={batchFilterParams}
                date={date}
                onSuccess={() => {
                    popupBatchDailyAttendanceUpdates.close()
                    listRollCallQuery.refetch()
                }}
            />
            <BatchDailyAttendanceCheckin
                isShow={popupBatchDailyAttendanceCheckin.isVisible}
                onClose={popupBatchDailyAttendanceCheckin.close}
                filterParams={batchFilterParams}
                date={date}
                onSuccess={() => {
                    listRollCallQuery.refetch()
                }}
            />
        </div>
    )
}
export const RollCall = KlassappTableHOC(RollCallTable)
